/*
 * @providesModule AccountManagementEmailCell
 *
 */

import React from 'react';
import _ from 'lodash';
import AccountInfoModal from 'AccountInfoModal';
import AWSManager from 'AWSManager';
import EmailVerifyController from 'EmailVerifyController';
import I18n from 'INTL';
import Label from 'Label';
import LoadingIndicator from 'LoadingIndicator';
import Optional from 'Optional';
import PushButton from 'PushButton';
import styles from 'AccountManagementEmailCellStyle';
import SkillzImage from 'SkillzImage';
import SkillzStyle from 'SkillzStyle';
import Sizes from 'Sizes';
import VerifyPasswordModal from 'VerifyPasswordModal';

import {TouchableOpacity, View, Modal, StyleSheet} from 'react-native';

const {viewCornerRadiusSecondary} = SkillzStyle.fullStyles;

interface AccountManagementEmailCellProps {
  style?: Object;
  index: number;
  email: string;
  primary: boolean;
  verified: boolean;
  editEnabled: boolean;
  selectPayPalEnabled: boolean;
  selectPayPalEmail: (index: number) => void;
  selectedForPayPal: boolean;
  onCheckboxPress: (isChecked: boolean, email: string) => void;
  subtitleEnabled: boolean;
  onVerifyPassword: (email: string) => void;
}

interface AccountManagementEmailCellState {
  isChecked: boolean;
  modal: {
    isModalVisible: boolean;
    title?: string;
    body?: string;
    hideCancel?: boolean;
    cancelButtonText?: string;
    type?: 'makePrimaryVerified' | 'verifyEmail' | 'default' | 'verifyPassword';
    okButtonText?: string;
  };
}

export default class AccountManagementEmailCell extends React.PureComponent<
  AccountManagementEmailCellProps,
  AccountManagementEmailCellState
> {
  static defaultProps = {
    style: {},
    primary: false,
    verified: false,
    editEnabled: false,
    selectPayPalEnabled: false,
    selectPayPalEmail: (index: number) => {
      /* */
    },
    selectedForPayPal: false,
    onCheckboxPress: (isChecked: boolean, email: string) => {
      /* */
    },
    subtitleEnabled: true,
  };

  constructor(props: AccountManagementEmailCellProps) {
    super(props);
    this.state = {
      isChecked: !props.selectPayPalEnabled,
      modal: {
        isModalVisible: false,
      },
    };

    this._showPrimaryEmailModal = this._showInfoModal.bind(this, 'primaryEmail');
    this._showMakePrimaryVerified = this._showInfoModal.bind(this, 'makePrimaryVerified');
    this._showMakePrimaryNotVerified = this._showInfoModal.bind(this, 'makePrimaryNotVerified');

    this.INFO_MODALS = {
      makePrimaryVerified: {
        titleText: I18n.message('Make This Your Primary Email?'),
        bodyText: I18n.message(
          'Your primary email address is used for login and player communications'
        ),
        hideCancel: false,
        okButtonTitle: I18n.message('Next'),
      },
      makePrimaryNotVerified: {
        titleText: I18n.message('Secondary Email'),
        bodyText: I18n.message(
          'Your secondary email must be verified before it can be made the primary account email.'
        ),
        hideCancel: true,
        okButtonTitle: I18n.message('OK'),
      },
      primaryEmail: {
        titleText: I18n.message('Primary Email'),
        bodyText: I18n.message(
          'Your primary email address is used for login and player communications'
        ),
        hideCancel: true,
        okButtonTitle: I18n.message('OK'),
      },
    };

    this._sendEmailVerificationTokenBind = this._onVerifyPressBind.bind(this);
    this._resendEmailVerificationTokenBind = this._sendEmailVerificationToken.bind(this, true);
  }

  _onCellPress = () => {
    if (!this.props.selectPayPalEnabled) {
      this._onCheckBoxPress();
    } else {
      if (!this.props.verified) {
        if (this.props.selectPayPalEnabled) {
          this._sendEmailVerificationToken(false);
        }
      } else {
        if (!this.props.selectedForPayPal) {
          this.props.selectPayPalEmail(this.props.index);
        }
      }
    }
  };

  _onCheckBoxPress = () => {
    this.setState({isChecked: !this.state.isChecked}, () => {
      this.props.onCheckboxPress(this.state.isChecked, this.props.email);
    });
  };

  _getSubtitle(): string {
    let subtitle = '';
    if (this.props.primary) {
      subtitle = I18n.message('Primary Email');
    } else if (!this.props.selectPayPalEnabled) {
      subtitle = I18n.message('Make Primary');
    } else if (!this.props.verified) {
      subtitle = I18n.message('Verification needed to use for PayPal');
    }

    return subtitle;
  }

  _getSubtitleAccessibilityLabel(): string {
    let subtitle = '';
    if (this.props.primary) {
      subtitle = 'primary_email_' + this.props.index;
    } else if (!this.props.selectPayPalEnabled) {
      subtitle = 'make_primary_' + this.props.index;
    } else if (!this.props.verified) {
      subtitle = this.props.email;
    }

    return subtitle;
  }

  _getSubtitleOnPress(): () => void {
    if (!this.props.verified && this.props.selectPayPalEnabled) {
      return this._sendEmailVerificationTokenBind;
    } else if (this.props.primary) {
      return this._showPrimaryEmailModal;
    } else if (this.props.verified) {
      return this._showMakePrimaryVerified;
    } else {
      return this._showMakePrimaryNotVerified;
    }
  }

  _renderModal(): React.ReactNode {
    const {type} = this.state.modal;
    const onDone = type == 'makePrimaryVerified' ? this._onMakePrimaryNextPress : this._hideModal;
    const onCancel =
      type == 'verifyEmail' ? this._resendEmailVerificationTokenBind : this._hideModal;
    const verifyPasswordModal = (
      <VerifyPasswordModal
        onDone={this._onVerifyPasswordDone}
        onCancel={this._hideModal}
        bodyText={this.state.modal.body}
      />
    );
    const infoModal = (
      <AccountInfoModal
        titleText={this.state.modal.title}
        bodyText={this.state.modal.body}
        cancelButtonTitle={this.state.modal.cancelButtonText}
        hideCancelButton={this.state.modal.hideCancel}
        okButtonTitle={this.state.modal.okButtonText}
        onCancel={onCancel}
        onDone={onDone}
      />
    );

    return (
      <Modal
        animationType='fade'
        transparent={true}
        onRequestClose={this._hideModal}
        visible={this.state.modal.isModalVisible}
      >
        <View
          style={[
            StyleSheet.absoluteFill,
            {maxWidth: Sizes.screenWidth, maxHeight: Sizes.screenHeight},
          ]}
        >
          {type == 'verifyPassword' ? verifyPasswordModal : infoModal}
        </View>
      </Modal>
    );
  }

  _showErrorModal(body: string) {
    this.setState({
      modal: {isModalVisible: true, title: I18n.message('Error'), body: body, hideCancel: true},
    });
  }

  _showInfoModal = (key: string) => {
    if (key == 'makePrimaryVerified' || key == 'makePrimaryNotVerified') {
      AWSManager.trackEvent('MANAGE_EMAIL_MAKE_PRIMARY');
    }
    this.setState({
      modal: {
        isModalVisible: true,
        title: this.INFO_MODALS[key].titleText,
        body: this.INFO_MODALS[key].bodyText,
        hideCancel: this.INFO_MODALS[key].hideCancel,
        okButtonText: this.INFO_MODALS[key].okButtonTitle,
        type: key,
      },
    });
  };

  _showVerifyModal = () => {
    const copy = this.props.selectPayPalEnabled
      ? I18n.message('Check your email to complete your withdrawal')
      : I18n.message(
          "By verifying your email, you'll be eligible for exciting offers and invitations to private tournaments."
        );
    this.setState({
      modal: {
        isModalVisible: true,
        title: I18n.message('Verification Email Sent!'),
        body: copy,
        type: 'verifyEmail',
        cancelButtonText: I18n.message('Resend'),
        okButtonText: I18n.message('OK'),
      },
    });
  };

  _hideModal = () => {
    this.setState({modal: {isModalVisible: false}});
    LoadingIndicator.hide();
  };

  _onMakePrimaryNextPress = () => {
    const bodyText = this.props.selectPayPalEnabled
      ? I18n.message('Verify your password to add or verify a PayPal email')
      : I18n.message('Verify your password to switch your primary email.');

    this.setState({
      modal: {
        isModalVisible: true,
        body: bodyText,
        type: 'verifyPassword',
      },
    });
  };

  _onVerifyPasswordDone = (success: boolean) => {
    if (success) {
      this._hideModal();
      this.props.onVerifyPassword(this.props.email);
    }
  };

  _shouldIncludeInfoIcon(): boolean {
    if (!this.props.selectPayPalEnabled) {
      if (this.props.primary || (!this.props.primary && !this.props.verified)) {
        return true;
      }
    }

    return false;
  }

  _onVerifyPressBind = () => {
    AWSManager.trackEvent('MANAGE_EMAIL_VERIFY');
    this._sendEmailVerificationToken(false);
  };

  _sendEmailVerificationToken = (isResend: boolean) => {
    LoadingIndicator.show();
    EmailVerifyController.sendVerificationToken(this.props.email)
      .then((response: Object) => {
        if (!isResend) {
          this._showVerifyModal();
        }
        LoadingIndicator.hide();
      })
      .catch((error: unknown) => {
        LoadingIndicator.hide();
        this._showErrorModal(
          _.get(
            error,
            'responseObject[0].message',
            I18n.message("Uh oh! Doesn't look like that worked. Please try again.")
          )
        );
        console.debug(
          'Error sending email verification token from AccountManagementEmailCell',
          error
        );
      });
  };

  _renderCheckBox(): React.ReactNode {
    return (
      <TouchableOpacity
        style={styles.checkBoxContainer}
        onPress={this._onCheckBoxPress}
        accessibilityRole={'button'}
        accessible={true}
        accessibilityLabel={'email_checkbox_' + this.props.index}
      >
        {this._renderCheckBoxContent()}
      </TouchableOpacity>
    );
  }

  _renderCheckBoxContent(): React.ReactNode {
    if (this.state.isChecked) {
      return (
        <View style={styles.selectedCheckBox}>
          <SkillzImage
            style={styles.checkboxIcon}
            source={'ic_checkmark_green_skz.png'}
            color={'#FFF'}
            resizeMode={'stretch'}
          />
        </View>
      );
    } else {
      return <View style={styles.unSelectedCheckBox} />;
    }
  }

  _renderEmail(): React.ReactNode {
    return (
      <View style={styles.emailContainer}>
        <Label style={styles.emailNameText}>{this.props.email}</Label>
        {this._renderSubtitle()}
      </View>
    );
  }

  _renderSubtitle(): React.ReactNode | null {
    const subtitle = this._getSubtitle();

    if (!this.props.subtitleEnabled || !subtitle) {
      return null;
    }

    const onPress = this._getSubtitleOnPress();

    return (
      <TouchableOpacity
        style={styles.subTitleContainer}
        onPress={onPress}
        accessibilityLabel={this._getSubtitleAccessibilityLabel()}
        accessible={true}
        accessibilityRole={'button'}
      >
        <Label style={styles.subTitleText}>{subtitle}</Label>
        {this._renderInfoIcon()}
      </TouchableOpacity>
    );
  }

  _renderInfoIcon(): React.ReactNode | null {
    if (!this._shouldIncludeInfoIcon()) {
      return null;
    }

    return (
      <View style={styles.infoButton}>
        <SkillzImage
          source={'ic_info_skz.png'}
          color={'rgb(74, 144, 226)'}
          style={styles.infoIcon}
        />
      </View>
    );
  }

  _renderVerifyButton(): React.ReactNode {
    return (
      <PushButton
        style={styles.verifyButton}
        onPress={this._sendEmailVerificationTokenBind}
        colors={['#FFF']}
        title={I18n.message('Verify')}
        textStyle={styles.verifyButtonText}
        cornerRadius={viewCornerRadiusSecondary.borderRadius}
        accessibilityLabel={'verify_' + this.props.index}
      />
    );
  }

  _renderPaypalSelectedCheckmark(): React.ReactNode {
    return (
      <SkillzImage
        style={styles.checkmarkIcon}
        source={'ic_checkmark_green_skz.png'}
        color={'rgb(74, 144, 226)'}
        resizeMode={'contain'}
      />
    );
  }

  _renderContent(): React.ReactNode {
    const selectedForPaypalStyle =
      this.props.selectPayPalEnabled && this.props.primary
        ? Sizes.select({
            tablet: 40,
            other: 28,
          })
        : Sizes.select({
            tablet: 30,
            other: 18,
          });

    return (
      <View style={[styles.contentContainer, {paddingVertical: selectedForPaypalStyle}]}>
        <View style={[styles.emailWithCheck, styles.flexOne]}>
          <Optional test={this.props.editEnabled}>{this._renderCheckBox()}</Optional>
          {this._renderEmail()}
        </View>
        <Optional
          test={!this.props.verified && !this.props.editEnabled && !this.props.selectPayPalEnabled}
        >
          {this._renderVerifyButton()}
        </Optional>
        <Optional test={this.props.selectPayPalEnabled && this.props.selectedForPayPal}>
          {this._renderPaypalSelectedCheckmark()}
        </Optional>
      </View>
    );
  }

  render(): React.ReactNode {
    if (this.props.selectPayPalEnabled) {
      return (
        <>
          {this._renderModal()}
          <TouchableOpacity
            style={[styles.container, this.props.style]}
            onPress={this._onCellPress}
            disabled={!this.props.selectPayPalEnabled}
            accessibilityLabel={this.props.email}
            accessibilityRole={'button'}
            accessible={true}
          >
            {this._renderContent()}
          </TouchableOpacity>
        </>
      );
    }

    return (
      <>
        <TouchableOpacity
          style={[styles.container, this.props.style]}
          onPress={this._onCellPress}
          disabled={!this.props.selectPayPalEnabled}
          accessible={false}
        >
          {this._renderContent()}
        </TouchableOpacity>
        {this._renderModal()}
      </>
    );
  }
}
